// ห้องแชท, ข้อความ, ส่งข้อความ (หักคอยน์หลัง AI สำเร็จ), โน้ตความจำ, ของขวัญ — พอร์ตจาก Chat.gs
//  /chats/<uid>/<chatId>  /messages/<chatId>/<msgId> (id เรียงตามเวลา -> แบ่งหน้าด้วย orderByKey+limitToLast)  /memories/<chatId>
import {
  fb, fk, err, str, num, bool, nowIso, newId, dayKey, limitOf, parseJson, asArray, replaceVars, getRow, rowsOf, putRow, putNew,
  updateRow, incr, bump, readAll, settings, setting, pageByKey, acquireFlag, releaseFlag, rateLimit,
} from "./core";
import { getUser } from "./auth";
import { changeCoins, notify, vipState } from "./economy";
import { getCharacter, getPrompt, canSeeCharacter } from "./chars";
import {
  relationshipDelta, levelLabel, ROMANTIC_LEVELS, INTIMACY_LEVELS, looksExplicit,
} from "./ai";
import { getChatMemory, scheduleSummary, recentKeys, rememberReply } from "./memory";
import { answer as botAnswer, answerIntent } from "../ai/retrieval";
import { giftTier, giftIntent, nextCombo, giftRelDelta } from "../gift";
import { resolvePersona } from "../ai/persona";
import { rewriteReply } from "../ai/rewrite";
import { generate as llmGenerate, abort as llmAbort, isReady as llmReady, isMockEngine } from "../ai/engine";

export const WINDOW = 30; // ข้อความล่าสุดที่ใช้ทำ prompt

/** ตั้งค่าใน /settings : ให้ Qwen ปรับคำตอบ (ค่าเริ่มต้นเปิด ใช้เมื่อโมเดลพร้อมเท่านั้น) */
// ค่าเริ่มต้นปิด: Qwen 0.5B ปรับคำไทยผ่านด่านตรวจแค่ ~16% (ทดสอบแล้ว) — เปิดเองได้ใน /settings
export const rewriteEnabled = (prefs) => !!prefs && prefs.aiRewrite === true;

const chatPath = (uid, id) => `chats/${fk(uid)}/${fk(id)}`;
const msgBase = (chatId) => `messages/${fk(chatId)}`;

/** ข้อความล่าสุด WINDOW รายการ (เก่า->ใหม่ รวมที่ลบ) */
export async function recentWindow(chatId) {
  const rows = await fb.entries(msgBase(chatId), { orderBy: "$key", limitToLast: WINDOW });
  return rows.map(([, v]) => v);
}

export async function ownChat(id, uid) {
  const chat = await getRow(chatPath(uid, str(id, 80, "chatId", true)));
  if (!chat || chat.isDeleted === true) throw err("not_found", "ไม่พบแชท");
  return chat;
}

export async function defaultModel() {
  const list = (await readAll("ai_models")).filter((m) => m.isActive !== false).sort((a, b) => num(a.sort) - num(b.sort));
  return list.filter((m) => m.isDefault === true)[0] || list[0] || null;
}

export async function modelById(id) {
  if (id) {
    const m = await getRow(`ai_models/${fk(id)}`);
    if (m && m.isActive !== false) return m;
  }
  const d = await defaultModel();
  if (!d) throw err("server_config", "ยังไม่มีโมเดล AI (รัน setup)");
  return d;
}

const visibleToUser = (item, uid) => !!item && item.visibility !== "deleted" && (item.visibility === "public" || item.userId === uid);

function streakUpdate(chat) {
  const today = dayKey();
  let count = num(chat.streakCount);
  if (chat.streakLastDate === today) return { streakCount: count || 1, streakBest: Math.max(num(chat.streakBest), count || 1), streakLastDate: today };
  count = chat.streakLastDate === dayKey(-1) ? count + 1 : 1;
  return { streakCount: count, streakBest: Math.max(num(chat.streakBest), count), streakLastDate: today };
}
const clampRel = (v) => Math.max(-1000, Math.min(1000, v));
const chatView = (c) => ({ ...c, settings: c.settings || { bannedWords: [], langGuard: true, shortcuts: [] } });

// ---- รายการแชท / สร้าง / ดู / แก้ / ลบ ----------------------------------------------------
export async function chatsList(p, ctx) {
  let rows = rowsOf(await fb.get(`chats/${fk(ctx.uid)}`));
  const archived = bool(p.archived), q = String(p.q || "").toLowerCase();
  rows = rows.filter((c) => c.isDeleted !== true && (c.isArchived === true) === archived);
  const memo = {};
  const charOf = async (id) => {
    if (!id) return null;
    if (memo[id] === undefined) memo[id] = await getCharacter(id);
    return memo[id];
  };
  if (q) {
    const keep = [];
    for (const c of rows) {
      const ch = await charOf(c.characterId);
      if (String(c.name || "").toLowerCase().indexOf(q) !== -1 || (ch && String(ch.name).toLowerCase().indexOf(q) !== -1)) keep.push(c);
    }
    rows = keep;
  }
  rows.sort((a, b) => {
    if ((a.isPinned === true) !== (b.isPinned === true)) return a.isPinned === true ? -1 : 1;
    return String(b.latestMessageTime || b.createdAt) < String(a.latestMessageTime || a.createdAt) ? -1 : 1;
  });
  const off = Math.max(0, parseInt(p.cursor || 0, 10) || 0), limit = limitOf(p.limit, 30);
  const page = rows.slice(off, off + limit);
  const items = [];
  for (const c of page) {
    const ch = await charOf(c.characterId);
    items.push({ ...chatView(c), character: ch ? { id: ch.id, name: ch.name, image: ch.image } : null });
  }
  return { items, nextCursor: off + limit < rows.length ? String(off + limit) : null };
}

export async function chatCreate(p, ctx) {
  const characterId = p.characterId ? str(p.characterId, 80) : "";
  const universeId = p.universeId ? str(p.universeId, 80) : "";
  if (!characterId && !universeId) throw err("bad_request", "ต้องระบุ characterId หรือ universeId");
  let ch = null, un = null, title = "", greeting = "", image = "";
  if (characterId) {
    ch = await getCharacter(characterId);
    if (!canSeeCharacter(ch, ctx.uid, ctx.isSuper)) throw err("not_found", "ไม่พบตัวละคร");
    title = ch.name; greeting = ch.greeting; image = ch.image;
  } else {
    un = await getRow(`universes/${fk(universeId)}`);
    if (!un || !(un.createdBy === ctx.uid || ctx.isSuper || (un.status === "approved" && un.visibility !== "private"))) throw err("not_found", "ไม่พบจักรวาล");
    title = un.name; greeting = un.greeting; image = un.image;
  }
  let personaId = p.personaId ? str(p.personaId, 80) : "";
  const getP = (id) => getRow(`personas/${fk(id)}`);
  if (personaId && !visibleToUser(await getP(personaId), ctx.uid)) throw err("bad_request", "ไม่พบบทบาทที่เลือก");
  let scenarioId = p.scenarioId ? str(p.scenarioId, 80) : (ch && ch.defaultScenarioId) || "";
  if (scenarioId && !visibleToUser(await getRow(`scenarios/${fk(scenarioId)}`), ctx.uid)) scenarioId = "";
  if (!personaId && ch && ch.defaultPersonaId && visibleToUser(await getP(ch.defaultPersonaId), ctx.uid)) personaId = ch.defaultPersonaId;
  const model = await modelById(p.modelId);
  const prefs = ctx.user.preferences || {};
  const rel = (ch && ch.initialRelationship) || {};
  const now = nowIso();
  const id = newId("ct");
  let chat = await putRow(chatPath(ctx.uid, id), "ct", {
    id, userId: ctx.uid, characterId: characterId || null, universeId: universeId || null,
    name: str(p.name, 40) || title, chatImage: image || "", personaId: personaId || null, scenarioId: scenarioId || null,
    modelId: model.id, relationshipMode: prefs.relationshipModeDefault === true,
    romanticValue: num(rel.romanticValue), intimacyValue: num(rel.intimacyValue),
    isPinned: false, isArchived: false, isRead: true, isDeleted: false, latestMessageTime: now, messageCount: 0,
    streakCount: 0, streakBest: 0, streakLastDate: null, settings: { langGuard: true },
  });
  const persona = personaId ? await getP(personaId) : null;
  const msgs = [];
  if (greeting) {
    const g = replaceVars(greeting, (persona && persona.name) || ctx.user.displayName, ch ? ch.name : title);
    msgs.push(await putNew(msgBase(chat.id), "m", { chatId: chat.id, sender: "character", type: "text", content: g }));
    chat = await updateRow(chatPath(ctx.uid, chat.id), { lastPreview: g.slice(0, 60), messageCount: 1 });
  }
  if (ch) await incr(`characters/${fk(ch.id)}`, "chatCount", 1);
  if (un) await incr(`universes/${fk(un.id)}`, "chatCount", 1);
  return { chat: chatView(chat), messages: msgs };
}

export async function loadChatContext(chat) {
  const out = { character: null, universe: null, characters: [], persona: null, scenario: null, memory: null };
  if (chat.characterId) {
    out.character = await getCharacter(chat.characterId);
    if (out.character) out.character = { ...out.character, description: await getPrompt(chat.characterId) };
  }
  if (chat.universeId) {
    out.universe = await getRow(`universes/${fk(chat.universeId)}`);
    if (out.universe) {
      out.characters = (await Promise.all(asArray(out.universe.characterIds, 10).map((id) => getCharacter(id)))).filter(Boolean);
      out.character = { name: out.universe.name, description: out.universe.description, bio: out.universe.history, baseLanguage: out.universe.baseLanguage, image: out.universe.image };
    }
  }
  if (chat.personaId) out.persona = await getRow(`personas/${fk(chat.personaId)}`);
  if (chat.scenarioId) out.scenario = await getRow(`scenarios/${fk(chat.scenarioId)}`);
  out.memory = await getRow(`memories/${fk(chat.id)}`);
  return out;
}

export async function chatGet(p, ctx) {
  const chat = await ownChat(p.id, ctx.uid), c = await loadChatContext(chat);
  const model = chat.modelId ? await getRow(`ai_models/${fk(chat.modelId)}`) : null;
  const days = Math.max(1, Math.floor((Date.now() - new Date(chat.createdAt).getTime()) / 86400000) + 1);
  const v = await vipState(ctx.user);
  const card = c.character ? { id: c.character.id || null, name: c.character.name, tagline: c.character.tagline || null, image: c.character.image, creatorName: c.character.creatorName || null } : null;
  return {
    chat: chatView(chat), character: card, persona: c.persona, scenario: c.scenario, model, memory: c.memory, memoryLimit: num(v.tier.noteLimit),
    stats: { days, streakCount: num(chat.streakCount), streakBest: num(chat.streakBest), messageCount: num(chat.messageCount), since: chat.createdAt },
    relationship: {
      romanticValue: num(chat.romanticValue), romanticLabel: levelLabel(ROMANTIC_LEVELS, num(chat.romanticValue)),
      intimacyValue: num(chat.intimacyValue), intimacyLabel: levelLabel(INTIMACY_LEVELS, num(chat.intimacyValue)),
      enabled: chat.relationshipMode === true,
    },
  };
}

export async function chatUpdate(p, ctx) {
  const chat = await ownChat(p.id, ctx.uid), patch = {};
  if (p.name !== undefined) patch.name = str(p.name, 40, "ชื่อแชท", true);
  if (p.chatImage !== undefined) patch.chatImage = str(p.chatImage, 500);
  ["isPinned", "isArchived", "isRead", "relationshipMode"].forEach((k) => {
    if (p[k] !== undefined) patch[k] = bool(p[k]);
  });
  if (p.personaId !== undefined) {
    const pid = str(p.personaId, 80);
    if (pid && !visibleToUser(await getRow(`personas/${fk(pid)}`), ctx.uid)) throw err("bad_request", "ไม่พบบทบาทที่เลือก");
    patch.personaId = pid || null;
  }
  if (p.scenarioId !== undefined) {
    const sid = str(p.scenarioId, 80);
    if (sid && !visibleToUser(await getRow(`scenarios/${fk(sid)}`), ctx.uid)) throw err("bad_request", "ไม่พบสถานการณ์ที่เลือก");
    patch.scenarioId = sid || null;
  }
  if (p.modelId !== undefined) {
    const m = await getRow(`ai_models/${fk(str(p.modelId, 60))}`);
    if (!m || m.isActive === false) throw err("bad_request", "ไม่พบโมเดล");
    patch.modelId = m.id;
  }
  if (p.settings !== undefined) {
    const s = parseJson(p.settings, {}) || {};
    patch.settings = {
      bannedWords: asArray(s.bannedWords, 50).map((w) => String(w).slice(0, 60)),
      langGuard: s.langGuard !== false, shortcuts: asArray(s.shortcuts, 3),
    };
  }
  return { chat: chatView(await updateRow(chatPath(ctx.uid, chat.id), patch)) };
}

export async function chatDelete(p, ctx) {
  const chat = await ownChat(p.id, ctx.uid);
  await updateRow(chatPath(ctx.uid, chat.id), { isDeleted: true });
  return { ok: true };
}

// ---- ข้อความ ------------------------------------------------------------------------------
export async function messagesList(p, ctx) {
  const chat = await ownChat(p.chatId, ctx.uid);
  const r = await pageByKey(msgBase(chat.id), { limit: limitOf(p.limit, 30), cursor: p.cursor || "", filter: (m) => m.isDeleted !== true });
  return { items: r.items.reverse(), nextCursor: r.nextCursor };
}

async function ownMessage(p, uid) {
  const chat = await ownChat(p.chatId, uid);
  const m = await getRow(`${msgBase(chat.id)}/${fk(str(p.id, 80, "id", true))}`);
  if (!m || m.isDeleted === true) throw err("not_found", "ไม่พบข้อความ");
  return { m, chat };
}

export async function messageEdit(p, ctx) {
  const { m, chat } = await ownMessage(p, ctx.uid);
  if (m.sender === "system") throw err("forbidden", "แก้ข้อความระบบไม่ได้");
  const content = str(p.content, num(await setting("max_message_len"), 6000), "ข้อความ", true);
  return { message: await updateRow(`${msgBase(chat.id)}/${fk(m.id)}`, { content, editedAt: nowIso() }) };
}

export async function messageDelete(p, ctx) {
  const { m, chat } = await ownMessage(p, ctx.uid);
  await updateRow(`${msgBase(chat.id)}/${fk(m.id)}`, { isDeleted: true });
  return { ok: true };
}

export async function messageReact(p, ctx) {
  const { m, chat } = await ownMessage(p, ctx.uid);
  return { message: await updateRow(`${msgBase(chat.id)}/${fk(m.id)}`, { reaction: p.reaction === "like" ? "like" : null }) };
}

// ---- ส่งข้อความ -> AI -> หักคอยน์ (เมื่อ AI สำเร็จเท่านั้น) ----------------------------------------
export const chatSend = (p, ctx) => runTurn(p, ctx, false);
export const chatRegenerate = (p, ctx) => runTurn(p, ctx, true);

async function runTurn(p, ctx, regen) {
  const chat = await ownChat(p.chatId, ctx.uid);
  const st = await settings();
  const content = regen ? "" : str(p.content, num(st.max_message_len, 6000), "ข้อความ", true);
  rateLimit("send:" + ctx.uid, 40, 60);
  if (looksExplicit(content)) throw err("ai_blocked", "ข้อความนี้ไม่เหมาะสม AI ไม่สามารถตอบได้");

  const c = await loadChatContext(chat);
  if (!c.character) throw err("not_found", "ไม่พบตัวละครของแชทนี้");
  const model = await modelById(p.modelId || chat.modelId), cost = num(model.coinPerMessage);

  // 1) ตรวจยอดพอ (ยังไม่หัก)
  const fresh = await getUser(ctx.uid);
  if (num(fresh.coins) < cost) throw err("insufficient_coins", `คอยน์ไม่พอ ต้องการ ${cost} คอยน์ แต่มี ${num(fresh.coins)} คอยน์`);

  // 2) จองสิทธิ์ส่ง (กันส่งซ้อน/หักซ้ำ) — flag ใน RTDB ปล่อยเมื่อจบ
  if (!(await acquireFlag("send/" + ctx.uid, 90))) throw err("chat_busy", "กำลังตอบข้อความก่อนหน้าอยู่ รอสักครู่");
  try {
    let win = (await recentWindow(chat.id)).filter((m) => m.isDeleted !== true);
    let oldAi = null;
    if (regen) {
      for (let i = win.length - 1; i >= 0; i--) {
        if (win[i].sender === "character") {
          oldAi = win[i];
          break;
        }
      }
      if (!oldAi) throw err("bad_request", "ไม่มีข้อความให้เจนใหม่");
      win = win.filter((m) => m.id !== oldAi.id);
      if (!win.length || win[win.length - 1].sender !== "user") throw err("bad_request", "ไม่มีข้อความของผู้ใช้ให้ตอบ");
    }
    const userName = (c.persona && c.persona.name) || fresh.displayName || "ผู้ใช้", charName = c.character.name;
    const lastUserText = regen ? win[win.length - 1].content || "" : content;

    // เลือกคำตอบจากไฟล์ที่เตรียมไว้ตามบุคลิกของตัวละคร (ไม่เรียกโมเดลแต่งเอง) — ไม่ซ้ำกับ 3 ครั้งล่าสุดของแชทนี้
    const mem = await getChatMemory(chat.id);
    const recent = recentKeys(mem);
    const banned = (chat.settings && chat.settings.bannedWords) || [];
    let picked;
    try {
      picked = await botAnswer(resolvePersona(c.character), replaceVars(lastUserText, userName, charName), {
        recent, userName, charName, reject: banned.length ? (t) => banned.some((w) => w && t.indexOf(w) !== -1) : null,
      });
    } catch (e) {
      throw err("error", "โหลดชุดคำตอบของตัวละครไม่สำเร็จ ลองใหม่อีกครั้ง"); // ไม่เขียน ไม่หักคอยน์
    }
    // ให้ Qwen ปรับคำเล็กน้อย เฉพาะเมื่อโมเดลพร้อมอยู่แล้วและผู้ใช้ไม่ได้ปิด ; ไม่รอโหลดโมเดล ไม่ผ่านด่านตรวจ = ใช้ต้นฉบับ
    let aiText = picked.reply, rewrite = null;
    if (rewriteEnabled(fresh.preferences) && llmReady() && !isMockEngine() && !looksExplicit(aiText)) {
      rewrite = await rewriteReply(aiText, { generate: llmGenerate, onTimeout: llmAbort });
      if (rewrite.rewritten && !looksExplicit(rewrite.text)) aiText = rewrite.text;
    }
    aiText = aiText.slice(0, 6000);
    const charge = cost;
    const delta = relationshipDelta(lastUserText, chat);

    let balance = num((await getUser(ctx.uid)).coins);
    if (charge > 0) {
      const pay = await changeCoins(ctx.uid, -charge, { usageType: "message", refType: "chat", refId: chat.id, chatId: chat.id, characterId: chat.characterId, modelId: model.id, note: regen ? "เจนข้อความใหม่" : "ส่งข้อความ" });
      balance = pay.balance;
    }
    const saved = [];
    if (!regen) saved.push(await putNew(msgBase(chat.id), "m", { chatId: chat.id, userId: ctx.uid, sender: "user", type: "text", content, replyToId: p.replyToId || null, modelId: model.id }));
    const aiMsg = await putNew(msgBase(chat.id), "m", { chatId: chat.id, sender: "character", type: "text", content: aiText, modelId: model.id, coinCost: charge, romanticDelta: delta.romantic, intimacyDelta: delta.intimacy });
    saved.push(aiMsg);
    if (regen) await updateRow(`${msgBase(chat.id)}/${fk(oldAi.id)}`, { isDeleted: true });
    const stk = streakUpdate(chat);
    const newChat = await updateRow(chatPath(ctx.uid, chat.id), (cur) => ({
      latestMessageTime: nowIso(), lastPreview: aiText.slice(0, 60), isRead: true,
      messageCount: num(cur.messageCount) + (regen ? 0 : 2),
      romanticValue: clampRel(num(cur.romanticValue) + delta.romantic), intimacyValue: clampRel(num(cur.intimacyValue) + delta.intimacy), ...stk,
    }));
    if (!regen) {
      await incr(`users/${fk(ctx.uid)}`, "messageCount", 1);
      if (chat.characterId) await incr(`characters/${fk(chat.characterId)}`, "messageCount", 2);
      if (chat.universeId) await incr(`universes/${fk(chat.universeId)}`, "messageCount", 2);
    }
    if (charge > 0 && chat.characterId) await bump("chats", chat.characterId, 1);
    // สรุปความจำต่อ (ทุก ~8 ข้อความ) หลังตอบเสร็จ — ไม่ await ไม่บล็อกผู้ใช้
    await rememberReply(chat.id, picked.key, recent);
    if (!regen) scheduleSummary({ chatId: chat.id, win: [...win, ...saved], userName, charName, mem });
    return {
      userMessage: regen ? null : saved[0], aiMessage: aiMsg, coinCost: charge, coins: balance, delta, chat: chatView(newChat), mock: false, intent: picked.intent, rewritten: !!(rewrite && rewrite.rewritten),
    };
  } finally {
    await releaseFlag("send/" + ctx.uid);
  }
}

// ---- โน้ตความจำ ------------------------------------------------------------------------------
export async function memoryGet(p, ctx) {
  const chat = await ownChat(p.chatId, ctx.uid), m = await getRow(`memories/${fk(chat.id)}`), v = await vipState(ctx.user);
  return { memory: m, limit: num(v.tier.noteLimit), used: m ? num(m.charCount) : 0 };
}

export async function memorySave(p, ctx) {
  const chat = await ownChat(p.chatId, ctx.uid), v = await vipState(await getUser(ctx.uid)), limit = num(v.tier.noteLimit);
  const text = String(p.text === undefined ? "" : p.text);
  if (text.length > limit) throw err("bad_request", `โน้ตยาวเกินโควตา (${text.length}/${limit} ตัวอักษร) อัปเกรดคุยคลับเพื่อเพิ่มโควตา`);
  const ex = await getRow(`memories/${fk(chat.id)}`);
  const row = { text, charCount: text.length, isActive: p.isActive === undefined ? (ex ? ex.isActive !== false : true) : bool(p.isActive), userId: ctx.uid, chatId: chat.id };
  const m = ex ? await updateRow(`memories/${fk(chat.id)}`, row) : await putRow(`memories/${fk(chat.id)}`, "me", { id: chat.id, ...row });
  return { memory: m, limit };
}

// ---- ของขวัญ ---------------------------------------------------------------------------------
export async function giftSend(p, ctx) {
  const chat = await ownChat(p.chatId, ctx.uid);
  const gift = await getRow(`gifts/${fk(str(p.giftId, 60, "giftId", true))}`);
  if (!gift || gift.isActive === false) throw err("not_found", "ไม่พบของขวัญ");
  const ch = chat.characterId ? await getCharacter(chat.characterId) : null;
  const cost = num(gift.coinCost);
  const share = ch && ch.createdBy && ch.createdBy !== "system" && ch.createdBy !== ctx.uid ? Math.floor(cost * num(await setting("gift_creator_share"), 0.3)) : 0;
  const giftName = gift.nameTh || gift.name;
  const pay = await changeCoins(ctx.uid, -cost, { usageType: "gift", refType: "gift", refId: gift.id, chatId: chat.id, characterId: chat.characterId, note: "ส่ง " + giftName });
  const tx = await putNew("gift_tx", "gt", { userId: ctx.uid, creatorId: ch ? ch.createdBy : null, characterId: chat.characterId, chatId: chat.id, giftId: gift.id, giftName, coinCost: cost, creatorShare: share });
  if (share > 0) await incr(`users/${fk(ch.createdBy)}`, "creatorEarnings", share);
  // คอมโบ: ชิ้นเดิมซ้ำภายใน 3 วินาทีของข้อความของขวัญล่าสุดของผู้ใช้ในห้องนี้ (ทุกครั้งยังหักคอยน์ตามปกติ)
  const win = (await recentWindow(chat.id)).filter((m) => m.isDeleted !== true);
  const prevGift = [...win].reverse().find((m) => m.type === "gift" && m.sender === "user");
  const combo = nextCombo(prevGift, gift.id, Date.now());
  const tier = giftTier(cost);
  const msg = await putNew(msgBase(chat.id), "m", {
    chatId: chat.id, userId: ctx.uid, sender: "user", type: "gift", giftId: gift.id, giftName, giftIcon: gift.icon || gift.emoji || "🎁", coinCost: cost, tier, combo,
    content: `***ส่ง ${giftName} เป็นของขวัญ***`,
  });
  if (ch) {
    await incr(`characters/${fk(ch.id)}`, "giftCount", 1);
    await bump("gifts", ch.id, 1);
  }
  if (share > 0) await notify(ch.createdBy, "character", "มีคนส่งของขวัญให้ตัวละครของคุณ", `${giftName} (ได้รับ ${share} คอยน์)`);

  // ตัวละครดีใจ — เลือกคำตอบ gift_small/medium/big จากไฟล์ตามบุคลิกและมูลค่า (ไม่ซ้ำ 3 ล่าสุด ไม่หักคอยน์เพิ่ม ไม่ผูกกับ AI)
  const c = await loadChatContext(chat);
  const userName = (c.persona && c.persona.name) || ctx.user.displayName || "เธอ";
  let reply = null;
  try {
    if (c.character) {
      const mem = await getChatMemory(chat.id);
      const recent = recentKeys(mem);
      const banned = (chat.settings && chat.settings.bannedWords) || [];
      const picked = await answerIntent(resolvePersona(c.character), giftIntent(cost), {
        recent, userName, charName: c.character.name, giftName, reject: banned.length ? (t) => banned.some((w) => w && t.indexOf(w) !== -1) : null,
      });
      reply = picked.reply;
      await rememberReply(chat.id, picked.key, recent);
    }
  } catch (e) {
    reply = null;
  }
  if (!reply) reply = `ขอบคุณสำหรับ${giftName}นะ ดีใจมากเลย`;
  const delta = chat.relationshipMode === true ? giftRelDelta(cost) : { romantic: 0, intimacy: 0 };
  const ai = await putNew(msgBase(chat.id), "m", { chatId: chat.id, sender: "character", type: "text", content: String(reply).slice(0, 2000), intent: giftIntent(cost), romanticDelta: delta.romantic, intimacyDelta: delta.intimacy });
  const newChat = await updateRow(chatPath(ctx.uid, chat.id), (cur) => ({
    latestMessageTime: nowIso(), lastPreview: ai.content.slice(0, 60), messageCount: num(cur.messageCount) + 2,
    romanticValue: clampRel(num(cur.romanticValue) + delta.romantic), intimacyValue: clampRel(num(cur.intimacyValue) + delta.intimacy),
  }));
  return { giftTx: tx, message: msg, aiMessage: ai, coins: pay.balance, gift: { id: gift.id, name: giftName, icon: msg.giftIcon, coinCost: cost, tier, combo }, delta, chat: chatView(newChat), intent: giftIntent(cost) };
}
